import { EGuidedSearchComponentType, TGuestCountConfig, TGuidedSearchComponent } from './types';

type TStepsSelection = Record<string, Record<string, string | string[] | undefined>>;

// Converts a guided-search step selection into search query params. Handles both
// `button_group_radio` (params come from the selected row's query_params) and the
// address/dates/numeric fields (param keys come from the field's positional
// query_params, with values supplied by user input — or the field's JSON
// defaultValue when the user hasn't touched it, e.g. a numeric_input default).
//
// Shared by the locality redirect path (InlineGuidedSearch's customApplySelection)
// and the context's auto-advance applySelection so both build the same params and
// can't silently diverge — e.g. dropping the up-front address/dates/guests.
export const buildGuidedSearchParams = (
  steps: TGuidedSearchComponent[],
  selection: TStepsSelection,
): Record<string, string> => {
  const params: Record<string, string> = {};

  steps.forEach(step => {
    const stepSelection = selection[step.id] ?? {};

    step.fields.forEach(field => {
      const selected = stepSelection[field.id];

      if (field.component_type === EGuidedSearchComponentType.GuestOccupancy) {
        // Stored positionally as [adults, children, pet]. Counts fall back to their
        // JSON default; the pet key is emitted only when the toggle was on.
        const guests = field.properties.guests;
        const [adults, children, pet] = Array.isArray(selected) ? selected : [];

        const setCount = (config?: TGuestCountConfig, value?: string) => {
          if (!config?.queryKey) return;
          const raw = value || (config.defaultValue != null ? String(config.defaultValue) : '');
          const count = Number(raw);
          // Only emit positive counts — a count of 0 (e.g. children) contributes nothing.
          if (raw !== '' && !Number.isNaN(count) && count > 0) {
            params[config.queryKey] = String(count);
          }
        };

        setCount(guests?.adults, adults);
        setCount(guests?.children, children);

        if (guests?.pet?.queryKey && pet) {
          params[guests.pet.queryKey] = params[guests.pet.queryKey]
            ? `${params[guests.pet.queryKey]},${guests.pet.queryValue}`
            : guests.pet.queryValue;
        }
        return;
      }

      if (field.component_type === EGuidedSearchComponentType.ButtonGroupRadio) {
        if (!selected) return;
        const selectedRow = field.properties.rows?.find(row => row.id === selected);
        selectedRow?.query_params.forEach(param => {
          if (param.value === undefined) return;
          params[param.key] = params[param.key]
            ? `${params[param.key]},${param.value}`
            : param.value;
        });
        return;
      }

      // Address/dates/numeric fields: param keys from JSON, values from user input
      // (stored positionally — a single string for address/numeric, a string[] for
      // dates), falling back to the field's defaultValue (numeric_input) when the
      // user hasn't touched it. Empty parts contribute nothing.
      const inputValues = Array.isArray(selected) ? selected : [selected];
      (field.properties.query_params ?? []).forEach((param, index) => {
        const value = param.value ?? inputValues[index] ?? field.properties.defaultValue;
        if (param.key && value) {
          params[param.key] = String(value);
        }
      });
    });
  });

  return params;
};
